// AiUsage — /dashboard/ai-usage. Spec docs/specs/ai-token-quota.md §7.2.
//
// Four-section breakdown of this tenant's monthly AI token consumption:
//   1. Overview card (cap / used / percent / reset countdown / cost)
//   2. Per-feature breakdown (AI 對話 / 樓層平面圖：結構 / 家具)
//   3. Per-user breakdown table (誰用最多)
//   4. Per-day trend bar chart + recent events (50 latest)
//
// Gated by read:billing scope (sidebar entry + DASHBOARD_ROUTE_ROLES).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ServerCrash, Inbox, User, Coins } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import {
  getAiUsage,
  friendlyAiUsageError,
  featureLabelZh,
  roleLabelZh,
  type AiUsageResponse,
} from "../lib/aiUsageApi.js";

type Stage =
  | { status: "loading" }
  | { status: "ready"; data: AiUsageResponse }
  | { status: "error"; message: string };

function formatTokens(n: number): string {
  return new Intl.NumberFormat("zh-TW").format(n);
}
function formatUsd(n: number): string {
  return new Intl.NumberFormat("zh-TW", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);
}
function daysUntil(iso: string): number {
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((target - now) / (24 * 60 * 60 * 1000)));
}
function formatMonthLabelZh(month: string): string {
  // 'YYYY-MM' → '2026 年 5 月'
  const [y, m] = month.split("-");
  return `${y} 年 ${parseInt(m, 10)} 月`;
}
function formatDateShort(date: string): string {
  const d = new Date(date + "T00:00:00Z");
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}
function formatEventTime(iso: string): string {
  return new Date(iso).toLocaleString("zh-TW", { hour12: false });
}

const STATUS_TONE = (percent: number): string => {
  if (percent >= 95) return "text-danger";
  if (percent >= 80) return "text-warning";
  return "text-text-primary";
};
const BAR_TONE = (percent: number): string => {
  if (percent >= 95) return "bg-danger";
  if (percent >= 80) return "bg-warning";
  return "bg-accent";
};

export default function AiUsage(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [stage, setStage] = useState<Stage>({ status: "loading" });
  // Month selector — current month default. Persisted not needed (page is
  // single-purpose read-only).
  const [month, setMonth] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStage({ status: "loading" });
    try {
      const data = await getAiUsage({ month, tenantId: tenantParam });
      setStage({ status: "ready", data });
    } catch (err) {
      setStage({ status: "error", message: friendlyAiUsageError(err) });
    }
  }, [month, tenantParam]);

  useEffect(() => { void load(); }, [load]);

  // super_admin must pick a tenant before any data exists.
  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="AI 用量明細" description="本月每位使用者的 AI 對話與樓層解析 token 用量明細。" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="AI 用量明細"
        description={
          stage.status === "ready"
            ? `${formatMonthLabelZh(stage.data.month)}・全租戶共享配額`
            : "本月每位使用者的 AI 對話與樓層解析 token 用量明細。"
        }
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={stage.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${stage.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />

      {stage.status === "loading" && <AiUsageSkeleton />}
      {stage.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="AI 用量資料載入失敗"
          description={stage.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>}
        />
      )}
      {stage.status === "ready" && (
        <AiUsageContent
          data={stage.data}
          month={month ?? stage.data.month}
          onMonthChange={(m) => setMonth(m)}
        />
      )}
    </>
  );
}

interface ContentProps {
  data: AiUsageResponse;
  month: string;
  onMonthChange: (m: string | null) => void;
}

function AiUsageContent({ data, month, onMonthChange }: ContentProps): React.ReactElement {
  const totalCost = useMemo(
    () => data.breakdown_by_feature.reduce((s, f) => s + f.cost_usd, 0),
    [data.breakdown_by_feature],
  );
  const daysLeft = daysUntil(data.reset_at);

  // Month picker — current + previous 3 months. Date strings for the
  // <select> value match the spec's `month=YYYY-MM` query param.
  const monthOptions = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 4; i++) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      out.push({ value, label: formatMonthLabelZh(value) });
    }
    return out;
  }, []);

  return (
    <div className="space-y-6">
      {/* Month selector */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-text-muted">查看月份：</label>
        <select
          value={month}
          onChange={(e) => onMonthChange(e.target.value)}
          className="bg-bg-base border border-border rounded text-sm px-2 py-1 text-text-primary"
        >
          {monthOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* ── 1. Overview ──────────────────────────────────────────── */}
      <section className="card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-text-muted mb-1">本月用量</div>
            <div className={`text-4xl font-bold ${STATUS_TONE(data.percent_used)}`}>
              {data.percent_used}%
            </div>
            <div className="mt-1 text-xs text-text-secondary">
              已用 {formatTokens(data.used_month_tokens)} /
              {data.cap_month_tokens < 0
                ? " 無上限"
                : ` ${formatTokens(data.cap_month_tokens)} tokens`}
            </div>
            <div className="mt-3 h-2 rounded bg-bg-base overflow-hidden">
              <div
                className={`h-full transition-all ${BAR_TONE(data.percent_used)}`}
                style={{ width: `${data.percent_used}%` }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            <div>
              <div className="text-xs text-text-muted">本月成本</div>
              <div className="mt-1 text-lg font-semibold text-text-primary">{formatUsd(totalCost)}</div>
            </div>
            <div>
              <div className="text-xs text-text-muted">重置倒數</div>
              <div className="mt-1 text-lg font-semibold text-text-primary">
                {daysLeft} 天
              </div>
              <div className="text-2xs text-text-muted mt-0.5">
                {new Date(data.reset_at).toLocaleDateString("zh-TW")} 起重新計算
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 1.5 加值券池（只在 active 時顯示）─────────────────────── */}
      {data.grants_active_count > 0 && (
        <section className="card p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-raised border border-border">
              <Coins className="h-4 w-4 text-accent" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-text-primary">加值券池</h2>
              <p className="mt-0.5 text-2xs text-text-secondary leading-snug">
                平台管理員加值的一次性 token；不會月底重置、月度上限用完後才開始扣抵。
              </p>
              <div className="mt-3 grid grid-cols-3 gap-3 sm:gap-6">
                <div>
                  <div className="text-2xs text-text-muted">剩餘 token</div>
                  <div className="mt-0.5 text-lg font-semibold text-text-primary">{formatTokens(data.grants_remaining)}</div>
                </div>
                <div>
                  <div className="text-2xs text-text-muted">有效加值券</div>
                  <div className="mt-0.5 text-lg font-semibold text-text-primary">{data.grants_active_count} 張</div>
                </div>
                <div>
                  <div className="text-2xs text-text-muted">最近一張到期</div>
                  <div className="mt-0.5 text-sm font-medium text-text-primary">
                    {data.grants_next_expiry ? data.grants_next_expiry.slice(0, 10) : "無到期日"}
                  </div>
                </div>
              </div>
              {data.total_available !== -1 && (
                <div className="mt-3 pt-3 border-t border-border/40 text-2xs text-text-muted">
                  本月可用合計（月度餘額 + 加值券）：
                  <span className="ml-1 font-mono text-text-primary">{formatTokens(data.total_available)}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── 2. Per-feature ───────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-medium text-text-primary mb-3">按功能分項</h3>
        {data.breakdown_by_feature.length === 0 ? (
          <EmptyState icon={Inbox} title="本月尚無使用紀錄" description="跟 SENTRY 助手對話或解析樓層平面圖後，使用量會出現在這。" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.breakdown_by_feature.map((f) => {
              const sharePct = data.used_month_tokens > 0
                ? Math.round((f.total_tokens / data.used_month_tokens) * 100)
                : 0;
              return (
                <div key={f.feature} className="card p-4">
                  <div className="text-xs text-text-muted">{f.label_zh}</div>
                  <div className="mt-1 text-xl font-semibold text-text-primary">
                    {formatTokens(f.total_tokens)}
                  </div>
                  <div className="mt-0.5 text-2xs text-text-muted">
                    {sharePct}% 占比・{f.event_count} 次・{formatUsd(f.cost_usd)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── 3. Per-user ──────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-medium text-text-primary mb-3">按使用者分項</h3>
        {data.breakdown_by_user.length === 0 ? (
          <EmptyState icon={User} title="本月尚無使用者用量" description="—" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-text-muted">
                <tr className="border-b border-border">
                  <th className="text-left font-medium py-2 px-3">使用者</th>
                  <th className="text-left font-medium py-2 px-3">角色</th>
                  <th className="text-right font-medium py-2 px-3">總 tokens</th>
                  <th className="text-right font-medium py-2 px-3">成本</th>
                  <th className="text-right font-medium py-2 px-3">次數</th>
                  <th className="text-left font-medium py-2 px-3 w-1/4">占比</th>
                </tr>
              </thead>
              <tbody>
                {data.breakdown_by_user.map((u) => {
                  const sharePct = data.used_month_tokens > 0
                    ? Math.round((u.total_tokens / data.used_month_tokens) * 100)
                    : 0;
                  return (
                    <tr key={u.user_id ?? "system"} className="border-b border-border-subtle">
                      <td className="py-2 px-3 text-text-primary">{u.display_name ?? "（未知）"}</td>
                      <td className="py-2 px-3 text-text-secondary">{roleLabelZh(u.role)}</td>
                      <td className="py-2 px-3 text-right text-text-primary">{formatTokens(u.total_tokens)}</td>
                      <td className="py-2 px-3 text-right text-text-secondary">{formatUsd(u.cost_usd)}</td>
                      <td className="py-2 px-3 text-right text-text-muted">{u.event_count}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded bg-bg-base overflow-hidden">
                            <div className="h-full bg-accent" style={{ width: `${sharePct}%` }} />
                          </div>
                          <span className="text-2xs text-text-muted w-8 text-right">{sharePct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 4. Per-day trend ─────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-medium text-text-primary mb-3">每日趨勢</h3>
        {data.breakdown_by_day.length === 0 ? (
          <p className="text-xs text-text-muted">—</p>
        ) : (
          <div className="card p-4">
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={data.breakdown_by_day.map((d) => ({ ...d, date_short: formatDateShort(d.date) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date_short" tick={{ fill: "#888", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#888", fontSize: 11 }} tickFormatter={(v) => formatTokens(v)} />
                  <Tooltip
                    contentStyle={{ background: "#1a1a1a", border: "1px solid #333", fontSize: 12 }}
                    formatter={(value: number) => [formatTokens(value), "tokens"]}
                  />
                  <Bar dataKey="total_tokens" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {/* ── 5. Recent events (50 latest, audit-grade) ────────────── */}
      <section>
        <h3 className="text-sm font-medium text-text-primary mb-3">最近事件（50 筆）</h3>
        {data.recent_events.length === 0 ? (
          <p className="text-xs text-text-muted">—</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-text-muted">
                <tr className="border-b border-border">
                  <th className="text-left font-medium py-2 px-3">時間</th>
                  <th className="text-left font-medium py-2 px-3">使用者</th>
                  <th className="text-left font-medium py-2 px-3">功能</th>
                  <th className="text-left font-medium py-2 px-3">模型</th>
                  <th className="text-right font-medium py-2 px-3">輸入 tokens</th>
                  <th className="text-right font-medium py-2 px-3">輸出 tokens</th>
                  <th className="text-right font-medium py-2 px-3">總計</th>
                  <th className="text-right font-medium py-2 px-3">成本</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_events.map((e) => (
                  <tr key={e.id} className="border-b border-border-subtle">
                    <td className="py-2 px-3 text-text-secondary whitespace-nowrap">{formatEventTime(e.created_at)}</td>
                    <td className="py-2 px-3 text-text-primary">{e.user_display_name ?? (e.user_id ? "（已刪除使用者）" : "（系統）")}</td>
                    <td className="py-2 px-3 text-text-secondary">{featureLabelZh(e.feature)}</td>
                    <td className="py-2 px-3 text-text-muted">{e.model}</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{formatTokens(e.prompt_tokens)}</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{formatTokens(e.completion_tokens)}</td>
                    <td className="py-2 px-3 text-right text-text-primary font-medium">{formatTokens(e.total_tokens)}</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{e.cost_usd != null ? formatUsd(e.cost_usd) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function AiUsageSkeleton(): React.ReactElement {
  return (
    <div className="space-y-6">
      <Skeleton className="h-28 w-full" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

